import React from 'react';
import { useQuery } from '@tanstack/react-query';
import RefDataService from '@app/model/refdata/service/RefDataService';
import { CountriesQueryKeys } from '@app/model/refdata/service/RefDataQueryKeys';
import { UseFormReturn } from 'react-hook-form';
import { IntegrationFormValues } from './useIntegrationCard';
import BaseFormSelect from '@ui/form/input/FormSelect';
import { Integration, IntegrationType } from '@app/model/integration-settings/Integration';
import {
	HISTORY_MONTHS_CHOICES,
	INTERVAL_CHOICES,
	INTERVAL_LABEL,
	MONTHS_AHEAD_CHOICES,
	START_AT_CHOICES,
} from '../IntegrationSettings.utilities';

const option = (value: string, label: string) => ({ id: value, label, value });
const FormSelect: React.FunctionComponent<React.ComponentProps<typeof BaseFormSelect>> = props =>
    <BaseFormSelect {...props} onValueChange={value => props.form.setValue(props.name, value, { shouldDirty: true })} />;

// The provider select (decision 12: the provider changes only when the Integration is Off).
export const ProviderField: React.FunctionComponent<{ form: UseFormReturn<IntegrationFormValues>, integration: Integration }> = ({ form, integration }) => {
	const locked = integration.status !== 'NOT_SET_UP' && integration.enabled;
	return <div>
		<FormSelect form={form} name="provider" label="Provider" placeholder="Choose a provider" disabled={locked}
								options={integration.providers.filter(provider => provider.available).map(provider => option(provider.provider, provider.label))} />
		<p className="help">{locked ? 'Switch the integration off to change the provider.' : integration.providers.filter(provider => !provider.available).map(provider => provider.label).join(' and ') + ': coming soon.'}</p>
	</div>;
};

export const MonthsAheadField: React.FunctionComponent<{ form: UseFormReturn<IntegrationFormValues>, type: IntegrationType }> = ({ form, type }) =>
	<FormSelect form={form} name="monthsAhead" label="Months ahead"
							options={MONTHS_AHEAD_CHOICES[type].map(months => option(String(months), `${months} months`))} />;

export const HistoryField: React.FunctionComponent<{ form: UseFormReturn<IntegrationFormValues> }> = ({ form }) => <div>
	<FormSelect form={form} name="historyMonthsBack" label="Import history from"
							options={HISTORY_MONTHS_CHOICES.map(months => option(String(months), `${months} months back`))} />
	<p className="help">The Backfill tab imports this window.</p>
</div>;

export const IntervalField: React.FunctionComponent<{ form: UseFormReturn<IntegrationFormValues>, label: string, integration: Integration }> = ({ form, label, integration }) => {
	const choices = integration.interval === 'CUSTOM' ? [...INTERVAL_CHOICES, 'CUSTOM' as const] : INTERVAL_CHOICES;
	return <FormSelect form={form} name="interval" label={label} options={choices.map(interval => option(interval, INTERVAL_LABEL[interval]))} />;
};

export const StartAtField: React.FunctionComponent<{ form: UseFormReturn<IntegrationFormValues> }> = ({ form }) => <div>
	<FormSelect form={form} name="startAt" label="Start at" options={START_AT_CHOICES.map(hour => option(hour, hour))} />
	<p className="help">The time of the property. </p>
</div>;

// Provider timestamps use this zone; the property schedule retains its own timezone.
export const ApiTimezoneField: React.FunctionComponent<{ form: UseFormReturn<IntegrationFormValues> }> = ({ form }) => {
	const countries = useQuery({ queryKey: CountriesQueryKeys.all, queryFn: RefDataService.findCountries });
	const zones = [...new Set((countries.data ?? []).map(country => country.timezone).filter((zone): zone is string => !!zone?.trim()))].sort();
	return <div>
		<FormSelect form={form} name="sourceTimezone" label="API timezone" placeholder="Choose the provider timezone" isRequired
			isLoading={countries.isPending} disabled={countries.isPending || countries.isError}
			options={zones.map(zone => option(zone, zone))} />
		<p className="help">{countries.isError ? 'Could not load country timezones. Refresh and retry.' : 'Timezone used by the provider for reservation and cancellation timestamps. Start at uses the property timezone.'}</p>
	</div>;
};
